import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import MetaTags from 'components/common/MetaTags';
import {
  getRoleCategoryOptions,
  VOLUNTEER_FIELD_EVENT_OPTIONS,
  getVolunteerEventTypeLabel,
} from 'config/admin/volunteer';
import { deleteVolunteerRole, getVolunteerRoles } from 'api/volunteerApi';
import CustomNewButton from 'components/NewDesign/CustomNewButton/CustomNewButton';
import CustomNewConfirm from 'components/NewDesign/CustomNewConfirm';
import styles from './VolunteerRoles.module.css';
import MainContent from 'components/NewDesign/MainContent';
import PageHeader from 'components/NewDesign/PageHeader';
import VolunteerRoleEditPopup from 'components/admin/volunteer/VolunteerRoleEditPopup';
import {
  groupingBasisOf,
  groupWords,
  formatGroupRange,
} from 'utils/groupLabel';
import RoleDeletionImpactDialog from 'components/admin/volunteer/RoleDeletionImpactDialog';
import PinProtectedAction from 'components/common/PinProtected/PinProtectedAction';
import { getErrorMessage } from 'utils/api';
import { toast } from 'react-toastify';
import { ReactComponent as SearchIcon } from 'assets/images/icon_search.svg';
import { allEventOptions } from 'config/admin/meet';
import { TRACK_EVENTS_CODE } from 'constants/eventTypes';

const VolunteerRoles = () => {
  const { selectedClub } = useSelector((state) => state.user || {});
  // Age Group, or Year Group for a club that groups by year level: the
  // category's name, its filter option and the column heading.
  const words = groupWords(groupingBasisOf(selectedClub));
  const roleCategoryOptions = useMemo(
    () => getRoleCategoryOptions(words),
    [words]
  );
  const [volunteerRoles, setVolunteerRoles] = useState([]);
  const [fetchingRoles, setFetchingRoles] = useState(true);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [selectedRole, setSelectedRole] = useState();
  const [editMode, setEditMode] = useState();

  // Filter state
  const [searchText, setSearchText] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('');

  // Delete confirmation state. Deletion follows the confirm-and-cascade
  // impact protocol: the initial delete may come back 409 with an impact
  // report, which swaps the basic confirm for the impact dialog.
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [impactReport, setImpactReport] = useState(null);
  const [showImpactDialog, setShowImpactDialog] = useState(false);

  const fetchVolunteerRoles = useCallback(async () => {
    try {
      setFetchingRoles(true);
      const response = await getVolunteerRoles(selectedClub?.clubID);
      if (response.data) {
        setVolunteerRoles(
          response.data.map((role) => {
            return {
              ...role,
              categoryLabel:
                roleCategoryOptions.find(
                  (opt) => opt.value === role.roleCategory
                )?.label || '',
            };
          })
        );
      }
    } catch (err) {
      console.log('[VolunteerRoles] Fetch Volunteer Roles error: ', err);
    } finally {
      setFetchingRoles(false);
    }
  }, [selectedClub?.clubID, roleCategoryOptions]);

  useEffect(() => {
    fetchVolunteerRoles();
  }, [fetchVolunteerRoles]);

  // Get unique categories for dropdown
  const categories = useMemo(() => {
    return roleCategoryOptions.map((opt) => opt.label);
  }, [roleCategoryOptions]);

  // Get unique event types across the roles for the dropdown, labelled the
  // same way as the table badges.
  const eventTypeOptions = useMemo(() => {
    const codes = new Set();
    volunteerRoles.forEach((role) => {
      (role.eventTypeMapping || []).forEach((val) => codes.add(+val));
    });
    const options = [];
    if (codes.has(TRACK_EVENTS_CODE)) {
      options.push({ value: TRACK_EVENTS_CODE, label: 'Track Events' });
      codes.delete(TRACK_EVENTS_CODE);
    }
    allEventOptions.forEach((opt) => {
      if (codes.has(+opt.value)) {
        options.push({ value: +opt.value, label: opt.label });
        codes.delete(+opt.value);
      }
    });
    VOLUNTEER_FIELD_EVENT_OPTIONS.forEach((opt) => {
      if (codes.has(opt.value)) {
        options.push({ value: opt.value, label: opt.label });
        codes.delete(opt.value);
      }
    });
    codes.forEach((val) => options.push({ value: val, label: String(val) }));
    return options;
  }, [volunteerRoles]);

  // Filter volunteer roles based on search, category and event type
  const filteredRoles = useMemo(() => {
    return volunteerRoles.filter((role) => {
      // Filter by category
      if (categoryFilter && role.categoryLabel !== categoryFilter) {
        return false;
      }

      // Filter by event type
      if (
        eventTypeFilter &&
        !(role.eventTypeMapping || []).some((val) => +val === +eventTypeFilter)
      ) {
        return false;
      }

      // Filter by search text
      if (searchText) {
        const search = searchText.toLowerCase();
        return (
          role.roleName?.toLowerCase().includes(search) ||
          role.categoryLabel?.toLowerCase().includes(search) ||
          role.ageGroupCode?.toLowerCase().includes(search)
        );
      }

      return true;
    });
  }, [volunteerRoles, searchText, categoryFilter, eventTypeFilter]);

  const handleAddRole = () => {
    setShowEditPopup(true);
    setEditMode('add');
  };

  const handleEditRole = (role) => {
    setShowEditPopup(true);
    setEditMode('edit');
    setSelectedRole(role);
  };

  const handleDeleteRole = (role) => {
    setSelectedRole(role);
    setShowDeleteConfirm(true);
  };

  const handleEditPopupClose = async (refresh) => {
    setShowEditPopup(false);
    setEditMode(null);
    setSelectedRole(null);
    if (refresh) {
      await fetchVolunteerRoles();
    }
  };

  const closeDeleteDialogs = () => {
    setShowDeleteConfirm(false);
    setShowImpactDialog(false);
    setImpactReport(null);
    setSelectedRole(null);
  };

  const performDeleteRole = async (cancelSignupIds) => {
    if (!selectedClub?.clubID || !selectedRole) {
      return;
    }
    try {
      setIsDeleting(true);
      await deleteVolunteerRole(
        selectedClub.clubID,
        selectedRole.volunteerRoleID,
        cancelSignupIds
      );
      toast.success(`Volunteer role "${selectedRole.roleName}" deleted`);
      closeDeleteDialogs();
      await fetchVolunteerRoles();
    } catch (err) {
      if (err?.response?.status === 409 && err.response.data) {
        // The role has open signups on upcoming meets — show (or refresh)
        // the impact report for explicit confirmation. selectedRole is kept
        // for the confirmed resubmit.
        setImpactReport(err.response.data);
        setShowDeleteConfirm(false);
        setShowImpactDialog(true);
      } else {
        console.error(err);
        toast.error(getErrorMessage(err));
        closeDeleteDialogs();
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const confirmDeleteRole = async () => {
    await performDeleteRole();
  };

  const confirmDeleteRoleWithImpact = async (resolutions) => {
    await performDeleteRole(resolutions?.cancelSignupIds || []);
  };

  const renderEventTypeMapping = (role) => {
    // Check if this is an Age Group role with no event types
    if (
      role.roleCategory === 'AgeGroupSpecific' &&
      (!role.eventTypeMapping || role.eventTypeMapping.length === 0)
    ) {
      return <span className={styles.badgeAllEvents}>All Events</span>;
    }

    if (!role.eventTypeMapping || role.eventTypeMapping.length === 0) {
      return '-';
    }

    // Original logic for when event types exist
    const eventTypes = role.eventTypeMapping.map((val) => {
      if (+val === TRACK_EVENTS_CODE) {
        return { label: 'Track Events', className: styles.badgeTrackEvents };
      }
      return {
        label: getVolunteerEventTypeLabel(val),
        className: styles.badgeEventType,
      };
    });

    return (
      <div className={styles.eventTypeBadges}>
        {eventTypes.map((eventType, index) => (
          <span key={index} className={eventType.className}>
            {eventType.label}
          </span>
        ))}
      </div>
    );
  };

  return (
    <>
      <MetaTags title="SplitFast | Volunteer Roles" />
      <MainContent loading={fetchingRoles}>
        <PinProtectedAction clubId={selectedClub?.clubID}>
          {({ requestPinValidation }) => {
            // Add/edit/delete are admin PIN gated — the PIN is validated
            // server-side, then the existing popup/confirm flow proceeds.
            const addWithPin = () =>
              requestPinValidation(
                'add-volunteer-role',
                handleAddRole,
                null,
                'Managing volunteer roles is restricted to admin users with PIN code access. Enter the admin PIN to add a new volunteer role.'
              );
            const editWithPin = (role) =>
              requestPinValidation(
                'edit-volunteer-role',
                handleEditRole,
                role,
                `Managing volunteer roles is restricted to admin users with PIN code access. Enter the admin PIN to edit the role "${role.roleName}".`
              );
            const deleteWithPin = (role) =>
              requestPinValidation(
                'delete-volunteer-role',
                handleDeleteRole,
                role,
                `Managing volunteer roles is restricted to admin users with PIN code access. Enter the admin PIN to delete the role "${role.roleName}".`
              );
            return (
              <>
                <PageHeader
                  title="Volunteer Roles"
                  subTitle="Manage the volunteer roles needed to run your meets"
                  primarySubTitle
                  regularWeightSubTitle
                >
                  <div className={styles.buttonContainer}>
                    <CustomNewButton
                      label="+ Add Role"
                      onClick={addWithPin}
                      style={{ marginLeft: 'auto' }}
                      variant="primary"
                    />
                  </div>
                </PageHeader>
                <div className={styles.tableCard}>
                  {/* Filter Bar */}
                  <div className={styles.filterBar}>
                    <div className={styles.searchWrapper}>
                      <input
                        type="text"
                        className={styles.searchInput}
                        placeholder="Search volunteer roles..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                      />
                      <SearchIcon className={styles.searchIcon} />
                    </div>
                    <div className={styles.filterGroup}>
                      <label
                        htmlFor="categoryFilter"
                        className={styles.filterLabel}
                      >
                        Category:
                      </label>
                      <select
                        id="categoryFilter"
                        className={styles.filterSelect}
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                      >
                        <option value="">All</option>
                        {categories.map((category) => (
                          <option key={category} value={category}>
                            {category}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className={styles.filterGroup}>
                      <label
                        htmlFor="eventTypeFilter"
                        className={styles.filterLabel}
                      >
                        Event Type:
                      </label>
                      <select
                        id="eventTypeFilter"
                        className={styles.filterSelect}
                        value={eventTypeFilter}
                        onChange={(e) => setEventTypeFilter(e.target.value)}
                      >
                        <option value="">All</option>
                        {eventTypeOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Table */}
                  <div className={styles.tableWrapper}>
                    <table className={styles.dataTable}>
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Category</th>
                          <th>Hours</th>
                          <th>Event Type</th>
                          <th>{words.group}</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRoles.length === 0 ? (
                          <tr>
                            <td colSpan={6} className={styles.emptyState}>
                              No volunteer roles found
                            </td>
                          </tr>
                        ) : (
                          filteredRoles.map((role) => (
                            <tr key={role.volunteerRoleID}>
                              <td className={styles.roleName}>
                                {role.roleName}
                              </td>
                              <td className={styles.categoryCell}>
                                {role.categoryLabel}
                              </td>
                              <td>{role.hoursEarned}</td>
                              <td className={styles.eventTypeCell}>
                                {renderEventTypeMapping(role)}
                              </td>
                              <td>
                                {formatGroupRange(
                                  groupingBasisOf(selectedClub),
                                  role.ageGroupCode
                                ) || '-'}
                              </td>
                              <td className={styles.actionsCell}>
                                <button
                                  onClick={() => editWithPin(role)}
                                  className={styles.actionButton}
                                  title="Edit Role"
                                >
                                  ✏️
                                </button>
                                <button
                                  onClick={() => deleteWithPin(role)}
                                  className={styles.actionButton}
                                  title="Delete Role"
                                >
                                  🗑️
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <VolunteerRoleEditPopup
                  clubID={selectedClub?.clubID}
                  isOpen={showEditPopup}
                  onClose={(e) => handleEditPopupClose(e)}
                  role={selectedRole}
                  mode={editMode}
                />
                {/* Delete Confirmation Dialog */}
                <CustomNewConfirm
                  isOpen={showDeleteConfirm}
                  onClose={closeDeleteDialogs}
                  title="Delete Volunteer Role"
                  description={`Delete the volunteer role "${selectedRole?.roleName || ''}"?`}
                  confirmButtonText="Delete Volunteer Role"
                  confirmButtonVariant="danger"
                  onConfirm={confirmDeleteRole}
                  cancelButtonText="Cancel"
                  showWarning={true}
                  warningText="The role is removed from your club's role list and from any meets that haven't been completed. Completed meets keep their history — awarded points and hours are not affected. If the role has signups on upcoming meets, you'll be asked to review them first."
                  isLoading={isDeleting}
                />
                {/* Impact review — shown when the delete returns 409 with a report */}
                <RoleDeletionImpactDialog
                  isOpen={showImpactDialog}
                  report={impactReport}
                  onCancel={closeDeleteDialogs}
                  onConfirm={confirmDeleteRoleWithImpact}
                  deleting={isDeleting}
                />
              </>
            );
          }}
        </PinProtectedAction>
      </MainContent>
    </>
  );
};

export default VolunteerRoles;
